import { createEffect } from "solid-js";
import { useLocation, useNavigate } from "@solidjs/router";

const PARAM = "dealer";
const KEY = "lob-locked-dealer";

/**
 * A D365 customer number: one to twelve digits. Anything else in the parameter is no lock at all — not
 * an error, and not a reason to drop the one the tab already holds.
 *
 * The chains' GTM containers read the key and the envelope's `locked_dealer_id` in this same shape, so
 * the name, the format and the meaning are a contract with them (docs/tracking/DATALAYER-KONTRAKT.md).
 */
const ACCOUNT = /^\d{1,12}$/;

function account(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  return trimmed && ACCOUNT.test(trimmed) ? trimmed : null;
}

/**
 * The account taken from this page load's URL, for a tab whose storage is disabled: it outlives the
 * in-app navigation that drops the query string, though not a reload.
 */
let captured: string | null = null;

function fromUrl(): string | null {
  try {
    return account(new URL(window.location.href).searchParams.get(PARAM));
  } catch {
    return null;
  }
}

/**
 * Takes a `?dealer=<ERP account number>` link into this tab's session. Called once at startup, before
 * anything navigates: the start page's button and the redirects on landing all go to a path of their
 * own, and the dealer is not asked until the summary step, several pages later.
 *
 * Held in sessionStorage, as the ?token= deep link is (see sessionStore), so a refresh on any page
 * keeps it. Per tab, so one browser can hold two dealers' links open. A load without the parameter
 * leaves an earlier one in place: the link lands on one page, and the customer walks on from there.
 * The URL itself is left alone — unlike a sales token this is no secret — and kept that way by
 * {@link useKeepDealerInUrl}.
 */
export function captureLockedDealer(): void {
  captured = fromUrl();
  if (!captured) return;
  try {
    sessionStorage.setItem(KEY, captured);
  } catch {
    // Storage disabled: `captured` carries it through this page load.
  }
}

/**
 * The dealer this session is pinned to, from a `?dealer=` link — see {@link captureLockedDealer}.
 *
 * A dealer hands out a link to the configurator; the end customer designs a door and may request a
 * quotation from that dealer and nobody else. The picker resolves to a single entry, which the summary
 * step already sends to without opening a modal — choosing between one option is a click that decides
 * nothing. The tracking envelope carries it too, as `locked_dealer_id`, so a chain's reports can tell
 * its stores apart.
 *
 * The lock is a convenience, not a permission: the server takes the account number from the request and
 * anyone can edit the URL. It decides which dealer a link asks, not who is allowed to be asked.
 */
export function lockedDealerAccount(): string | null {
  try {
    return account(sessionStorage.getItem(KEY)) ?? captured;
  } catch {
    return captured;
  }
}

/**
 * Keeps a `?dealer=` link's parameter in the address bar while the visitor moves around the app. Call
 * once, from the layout.
 *
 * The lock does not need it — the session holds that — but the chains' GTM containers fall back to the
 * address when nothing else says which store the visitor came from, and a customer who copies the
 * address to send on should pass the store with it. Every route change goes to a path of its own and
 * drops the query string, so the parameter is put back on the new address, in place, without a history
 * entry of its own.
 *
 * Only a parameter the address already carried is carried on. A lock the session holds from earlier in
 * the tab is not written into an address that did not have it.
 */
export function useKeepDealerInUrl(): void {
  const location = useLocation();
  const navigate = useNavigate();
  let carried: string | null = null;

  createEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(PARAM);
    if (raw !== null) {
      carried = account(raw);
      return;
    }
    if (!carried) return;

    params.set(PARAM, carried);
    navigate(`${location.pathname}?${params}${location.hash}`, {
      replace: true,
      scroll: false,
      state: location.state,
    });
  });
}
